import {
  EmptyState,
  FieldAlert,
  FlexBox,
  InputSelectionListProps,
  SelectionList,
  SimplePagination,
  Text,
  TextWithHighlight,
} from '@dixa/lib-shared-habla';
import {
  HookFormFieldWrappedBaseProps,
  HookFormSelectionList,
} from 'lib-shared-hook-form';
import { AsyncStatus } from 'lib-shared-types-api';
import { useEffect, useState } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

import { integrationsApi } from 'apis/integrations';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';

import styles from './hookFormEmailSelect.module.scss';

type HookFormEmailSelectProps = Partial<
  Omit<InputSelectionListProps<string>, 'children'>
> & {
  onEmailAsyncStatusChange?: (status: AsyncStatus) => void;
};

export const HookFormEmailSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: HookFormFieldWrappedBaseProps<
    TFieldValues,
    TName,
    HookFormEmailSelectProps
  >,
): JSX.Element => {
  const { onEmailAsyncStatusChange, ...restComponentProps } =
    props.componentProps ?? {};
  const [filter, setFilter] = useState('');

  const {
    items: emails,
    asyncStatus: getEmailAsyncStatus,
    changeArg,
    nextPage,
    previousPage,
    hasNextPage,
    hasPreviousPage,
  } = usePaginatedEndpoint({
    endpoint: integrationsApi.endpoints.readEmailIntegrations,
    itemsSelector: ({ data: { emails } }) => emails || [],
    initialArg: {
      limit: 50,
      offset: 0,
      sort: { key: 'email_address', direction: 'asc' },
    },
  });

  useEffect(() => {
    onEmailAsyncStatusChange?.(getEmailAsyncStatus);
  }, [getEmailAsyncStatus, onEmailAsyncStatusChange]);

  const hasNoEmails = !emails.length;
  const showNoIntegrationWarning =
    getEmailAsyncStatus === 'fulfilled' && hasNoEmails;

  return (
    <>
      <HookFormSelectionList
        {...props}
        rules={
          props.rules ?? {
            required: {
              value: true,
              message: 'Please select an email',
            },
          }
        }
        componentProps={{
          placeholder: 'Select email',
          getItemTitleByValue: (emailAddress) => {
            const integration = emails.find(
              (e) => e.email_address === emailAddress,
            );
            const label = integration?.sender_name
              ? `${integration.sender_name} <${emailAddress}>`
              : emailAddress;
            return <Text.Paragraph>{label}</Text.Paragraph>;
          },
          dropdownProps: {
            className: styles.selectDropdown,
          },
          filter: {
            value: filter,
            onChange: (event) => {
              const searchTerm = event.target.value;

              setFilter(searchTerm);
              changeArg(
                { searchTerm, offset: 0 },
                {
                  debounceTime: 300,
                },
              );
            },
          },
          onToggleDropdown: (isOpen: boolean) => {
            // When the user closes the dropdown with a previous
            // search term, we reset so the email list gets fetched
            // once again
            if (!isOpen && filter !== '') {
              setFilter('');
              changeArg({ searchTerm: '', offset: 0 });
            }
          },
          ...restComponentProps,
        }}
      >
        <>
          {emails.map((email) => (
            <SelectionList.Item
              key={email.email_address}
              value={email.email_address}
            >
              {filter ? (
                <TextWithHighlight
                  text={email.email_address}
                  highlight={filter}
                />
              ) : (
                <Text.Paragraph>
                  {email.sender_name} {'<'}
                  {email.email_address}
                  {'>'}
                </Text.Paragraph>
              )}
            </SelectionList.Item>
          ))}

          {hasNoEmails && (
            <SelectionList.Item
              noFixedSelectionStateSpace
              className={styles.selectDropdownRow}
            >
              <EmptyState
                title="No items found"
                description="Try changing your search criteria."
              />
            </SelectionList.Item>
          )}

          <FlexBox className={styles.selectDropdownPagination}>
            <SimplePagination
              {...(getEmailAsyncStatus !== 'uninitialized' || emails.length > 0
                ? {
                    nextButtonClick: nextPage,
                    previousButtonClick: previousPage,
                    nextButtonDisabled:
                      !hasNextPage || getEmailAsyncStatus !== 'fulfilled',
                    previousButtonDisabled:
                      !hasPreviousPage || getEmailAsyncStatus !== 'fulfilled',
                  }
                : undefined)}
            />
          </FlexBox>
        </>
      </HookFormSelectionList>

      {showNoIntegrationWarning && (
        <FieldAlert variant="warning">No email integration found.</FieldAlert>
      )}
    </>
  );
};
